import React, { useEffect } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { RootState, AppDispatch } from "../../store";
import { checkAuth, fetchUserPermissions } from "../../store/slices/authSlice";
import { LoadingOverlay } from "../common/LoadingOverlay";
import { usePermissions } from "../../hooks/usePermissions";

interface ProtectedRouteProps {
  children: React.ReactNode;
  requiredPermission?: string;
}

// Module-level variable to track permission loading
let isLoadingPermissions = false;

export const ProtectedRoute: React.FC<ProtectedRouteProps> = ({
  children,
  requiredPermission,
}) => {
  const dispatch = useDispatch<AppDispatch>();
  const location = useLocation();
  const { isAuthenticated, isLoading, permissions } = useSelector(
    (state: RootState) => state.auth
  );
  const { hasPermission } = usePermissions();

  useEffect(() => {
    const loadPermissions = async () => {
      if (isLoadingPermissions) {
        return;
      }

      const accessToken = localStorage.getItem("accessToken");

      if (accessToken && permissions.length === 0) {
        isLoadingPermissions = true;
        try {
          await dispatch(fetchUserPermissions()).unwrap();
        } catch (error) {
          await dispatch(checkAuth()).unwrap();
        }
      } else if (!isAuthenticated && !isLoading) {
        isLoadingPermissions = true;
        await dispatch(checkAuth()).unwrap();
      }
    };

    loadPermissions();
  }, []);

  if (isLoading || (isAuthenticated && permissions.length === 0)) {
    return <LoadingOverlay tip="Checking permissions..." />;
  }

  if (!isAuthenticated) {
    isLoadingPermissions = false;
    return <Navigate to="/login" state={{ from: location }} replace />;
  }

  if (requiredPermission && !hasPermission(requiredPermission)) {
    return <Navigate to="/unauthorized" replace />;
  }

  return <>{children}</>;
};
